import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, DateInput, FORM_CONTROL_CLASS, FORM_LABEL_CLASS } from '@safetyhub/ui';
import {
  SITE_WORKER_EMPLOYMENT_TYPE_LABELS,
  SITE_WORKER_EMPLOYMENT_TYPES,
  formatDateToLocal,
  type SiteWorkerEmploymentTypeValue,
  type SiteWorkerView,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { todayDateInputInTimezone } from '../../lib/localDate';
import { createSiteWorker, deleteSiteWorker, getSiteWorkers, legalWorkerRosterQueryKeys, updateSiteWorker } from '../../lib/legalWorkerRoster';
import { useToastStore } from '../../stores/toast';
import { fulfilledAtToIso } from './EvidenceModal';

/** 서버 DTO 상한(`SITE_WORKER_NAME_MAX`)과 같은 값 */
const NAME_MAX_LENGTH = 50;

export interface SiteWorkerRosterPanelProps {
  companyId: string;
  siteId: string;
  canManage: boolean;
  /** 현장 유효 시간대 — 배치일·종료일 변환과 「오늘」 */
  timezone: string;
}

/**
 * 근로자(교육 대상자) **명부**(M3 ⓐ · 사용자 결정 2026-11-04) — 이름 · 고용 구분(시행규칙 별표 4 제1호) · 배치일 · 퇴직(종료)일만 적는다
 * (연락처·생년월일은 받지 않는다). 채용 시·작업내용 변경 시·특별교육 사건을 등록할 때 여기 근로자를 고르면 **사람마다** 항목이 서고 교육
 * 시간·둘째 기한이 구분에서 정해진다(`EventRegisterModal`). 교육 사건에 등록된 근로자는 지우지 않고 종료일로 끝낸다(서버도 400).
 */
export default function SiteWorkerRosterPanel({ companyId, siteId, canManage, timezone }: SiteWorkerRosterPanelProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const queryKey = legalWorkerRosterQueryKeys.workers(companyId, siteId);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getSiteWorkers(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });
  const today = todayDateInputInTimezone(timezone);
  const [name, setName] = useState('');
  const [employmentType, setEmploymentType] = useState<SiteWorkerEmploymentTypeValue | ''>('');
  const [placedOn, setPlacedOn] = useState(today);
  const [endingId, setEndingId] = useState<string | null>(null);
  const [endedOn, setEndedOn] = useState(today);

  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const onError = (fallback: string) => (err: unknown) => showToast(apiErrorMessage(err) ?? fallback, 'error');
  const createMutation = useMutation({
    mutationFn: () =>
      createSiteWorker(companyId, siteId, {
        name: name.trim(),
        employmentType: employmentType as SiteWorkerEmploymentTypeValue,
        placedAt: fulfilledAtToIso(placedOn, timezone),
      }),
    onSuccess: async () => {
      setName('');
      setEmploymentType('');
      setPlacedOn(today);
      await refresh();
    },
    onError: onError('근로자를 명부에 넣지 못했습니다.'),
  });
  const endMutation = useMutation({
    mutationFn: ({ workerId, endedAt }: { workerId: string; endedAt: string | null }) =>
      updateSiteWorker(companyId, siteId, workerId, { endedAt }),
    onSuccess: async () => {
      setEndingId(null);
      await refresh();
    },
    onError: onError('종료일을 저장하지 못했습니다.'),
  });
  const deleteMutation = useMutation({
    mutationFn: (workerId: string) => deleteSiteWorker(companyId, siteId, workerId),
    onSuccess: refresh,
    onError: onError('명부에서 지우지 못했습니다.'),
  });
  const busy = createMutation.isPending || endMutation.isPending || deleteMutation.isPending;
  const workers = data?.workers ?? [];
  const createBlocked = !name.trim() || !employmentType || !placedOn;

  return (
    <Card padding="md" radius="lg" elevation="sm" className="mt-4" data-testid="site-worker-roster">
      <h2 className="text-base font-bold text-ink-900">근로자(교육 대상자) 명부</h2>
      <p className="mt-1 text-xs text-ink-500">
        채용 시·작업내용 변경 시·특별교육 사건을 등록할 때 여기 근로자를 고르면 사람마다 항목이 생기고, 교육 시간과 둘째 기한이 고용 구분(시행규칙
        [별표 4] 제1호)에서 정해집니다. 이름·고용 구분·배치일·종료일만 적습니다.
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-ink-500">불러오는 중…</p>
      ) : error ? (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          {apiErrorMessage(error) ?? '근로자 명부를 불러오지 못했습니다.'}
        </p>
      ) : (
        <>
          {workers.length === 0 ? (
            <p className="mt-3 text-sm text-ink-500">등록한 근로자가 없습니다.</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5" aria-label="근로자 명부">
              {workers.map((w) => (
                <WorkerLine
                  key={w.id}
                  worker={w}
                  canManage={canManage}
                  busy={busy}
                  ending={endingId === w.id}
                  endedOn={endedOn}
                  today={today}
                  onStartEnd={() => {
                    setEndedOn(today);
                    setEndingId(w.id);
                  }}
                  onEndedOnChange={setEndedOn}
                  onConfirmEnd={() => endMutation.mutate({ workerId: w.id, endedAt: fulfilledAtToIso(endedOn, timezone) })}
                  onCancelEnd={() => setEndingId(null)}
                  onReinstate={() => endMutation.mutate({ workerId: w.id, endedAt: null })}
                  onDelete={() => deleteMutation.mutate(w.id)}
                />
              ))}
            </ul>
          )}
          {canManage && (
            <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end" data-testid="site-worker-add">
              <div>
                <label htmlFor="legal-worker-name" className={FORM_LABEL_CLASS}>
                  이름
                </label>
                <input
                  id="legal-worker-name"
                  className={FORM_CONTROL_CLASS}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  onChange={(e) => setName(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div>
                <label htmlFor="legal-worker-type" className={FORM_LABEL_CLASS}>
                  고용 구분
                </label>
                <select
                  id="legal-worker-type"
                  className={FORM_CONTROL_CLASS}
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value as SiteWorkerEmploymentTypeValue)}
                  disabled={busy}
                >
                  <option value="" disabled>
                    선택하세요
                  </option>
                  {SITE_WORKER_EMPLOYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {SITE_WORKER_EMPLOYMENT_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="legal-worker-placed-on" className={FORM_LABEL_CLASS}>
                  배치일
                </label>
                <DateInput id="legal-worker-placed-on" label="배치일" value={placedOn} onChange={setPlacedOn} max={today} disabled={busy} />
              </div>
              <Button size="sm" disabled={createBlocked || busy} onClick={() => createMutation.mutate()}>
                명부에 넣기
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function WorkerLine({
  worker,
  canManage,
  busy,
  ending,
  endedOn,
  today,
  onStartEnd,
  onEndedOnChange,
  onConfirmEnd,
  onCancelEnd,
  onReinstate,
  onDelete,
}: {
  worker: SiteWorkerView;
  canManage: boolean;
  busy: boolean;
  ending: boolean;
  endedOn: string;
  today: string;
  onStartEnd: () => void;
  onEndedOnChange: (v: string) => void;
  onConfirmEnd: () => void;
  onCancelEnd: () => void;
  onReinstate: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-semibold text-ink-800">{worker.name}</span>
      <Badge tone="slate" size="sm">
        {SITE_WORKER_EMPLOYMENT_TYPE_LABELS[worker.employmentType]}
      </Badge>
      <span className="text-xs text-ink-500">배치 {formatDateToLocal(new Date(worker.placedAt))}</span>
      {worker.endedAt ? (
        <Badge tone="amber" size="sm">
          종료 {formatDateToLocal(new Date(worker.endedAt))}
        </Badge>
      ) : (
        <Badge tone="emerald" size="sm" dot>
          재직
        </Badge>
      )}
      {worker.eventCount > 0 && <span className="text-xs text-ink-500">교육 사건 {worker.eventCount}건</span>}
      {canManage && !ending && (
        <span className="ml-auto flex gap-1">
          {worker.endedAt ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onReinstate}>
              재직으로
            </Button>
          ) : (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onStartEnd}>
              종료일 넣기
            </Button>
          )}
          {worker.eventCount === 0 && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onDelete}>
              지우기
            </Button>
          )}
        </span>
      )}
      {canManage && ending && (
        <span className="ml-auto flex items-center gap-1">
          <DateInput
            id={`legal-worker-ended-on-${worker.id}`}
            label="종료일"
            value={endedOn}
            onChange={onEndedOnChange}
            max={today}
            disabled={busy}
          />
          <Button size="sm" disabled={!endedOn || busy} onClick={onConfirmEnd}>
            저장
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={onCancelEnd}>
            취소
          </Button>
        </span>
      )}
    </li>
  );
}
